import { FC, forwardRef, useRef, useState, useEffect } from "react";
import { Box, Chip, Popover, Stack, TextField, Typography } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { RichTreeView, TreeItem, TreeItemProps, useTreeItemModel } from "@mui/x-tree-view";
import { GROUPS } from "../reviewStatus";

/**
 * Filter for the CRU/NRU/RSU review status facets. Each option value is a "<key>|<color>|<tag>"
 * string from the backend (see get_filter_values in records/utils.py), shown as a tree of
 * group (not selectable) > color (selects all its tags) > tag.
 */

const COLORS: Array<[string, string, string]> = [
	["red", "Action Needed", "#d32f2f"],
	["orange", "In Progress", "#f57c00"],
	["green", "Cleared", "#388e3c"],
	["grey", "Not Applicable", "#9e9e9e"],
	["#555555", "Pending Submission", "#555555"],
];

type StatusItem = {
	id: string;
	label: string;
	dot?: string;
	children?: StatusItem[];
};

const buildItems = (values: string[]): StatusItem[] => {
	// Pass 1: collect tags under their key and color
	const tree: Record<string, Record<string, StatusItem[]>> = {};
	for (const value of values) {
		const [key, color, tag] = value.split("|");
		tree[key] ??= {};
		tree[key][color] ??= [];
		tree[key][color].push({ id: value, label: tag });
	}

	// Pass 2: output in the fixed group and color order
	const items: StatusItem[] = [];
	for (const [key, groupLabel] of GROUPS) {
		// Group level
		if (!tree[key]) continue;
		const colorItems: StatusItem[] = [];
		for (const [color, colorLabel, dot] of COLORS) {
			// Color level; its children are the tag items from pass 1
			if (!tree[key][color]) continue;
			colorItems.push({ id: `color:${key}|${color}`, label: colorLabel, dot, children: tree[key][color] });
		}
		items.push({ id: `group:${key}`, label: groupLabel, children: colorItems });
	}
	return items;
};

/** Tree item that puts a colored dot before the label of color-level items. */
const StatusTreeItem = forwardRef<HTMLLIElement, TreeItemProps>((props, ref) => {
	const item = useTreeItemModel<StatusItem>(props.itemId);
	const label = item?.dot ? (
		<Stack direction="row" alignItems="center" gap="6px">
			<span style={{ width: 10, height: 10, borderRadius: "50%", background: item.dot, display: "inline-block", flexShrink: 0 }} />
			{props.label}
		</Stack>
	) : props.label;
	return <TreeItem {...props} ref={ref} label={label} />;
});

type Props = {
	label: string;
	options: Array<{ label: string; value: string }>;
	value: string[];
	onChange: (v: any) => void;
};

export const StatusFacetFilter: FC<Props> = ({ label, options, value, onChange }) => {
	const anchorRef = useRef<HTMLDivElement | null>(null);
	const [open, setOpen] = useState(false);
	const [localValue, setLocalValue] = useState(value);
	const [search, setSearch] = useState("");
	// Groups are always expanded; only colors are expanded/collapsed by the user
	const [expandedColors, setExpandedColors] = useState<string[]>([]);
	const visibleOptions = search
		? options.filter((o) =>
				o.label.toLowerCase().includes(search.toLowerCase()),
			)
		: options;
	const visibleValues = visibleOptions.map((o) => o.value);
	const items = buildItems(visibleValues);

	// To reset the selection after the user navigates back to the list page
	useEffect(() => {
		setLocalValue(value);
	}, [value]);

	/**
	 * `localValue` only has tags, but the tree decides whether a click selects or deselects an item
	 * by whether that item's own id is selected, not by its (descendant-derived) checkbox state.
	 * So add colors whose visible tags are all selected; otherwise clicking a color that looks
	 * checked would select it again instead of clearing it.
	 */
	const treeSelection = [...localValue];
	for (const groupItem of items) {
		for (const colorItem of groupItem.children ?? []) {
			if (colorItem.children?.every((tagItem) => localValue.includes(tagItem.id))) {
				treeSelection.push(colorItem.id);
			}
		}
	}

	/**
	 * `visibleSelectedIds` is what the tree reports as selected after a change. The tree only
	 * knows the items it currently shows, so this covers visible items only. It may also contain
	 * parent (color/group) ids, which aren't filter values and are dropped. Selected tags hidden by
	 * the search aren't in the tree and never reported, so they are carried over from `localValue`.
	 */
	const handleTreeSelection = (visibleSelectedIds: string[]) => {
		// Hidden selected tags
		const newValue = localValue.filter((v) => !visibleValues.includes(v));
		// Visible selected tags (dropping color and group ids)
		for (const id of visibleSelectedIds) {
			if (!id.startsWith("color:") && !id.startsWith("group:")) newValue.push(id);
		}
		setLocalValue(newValue.sort());
	};

	const removeSelection = (optionValue: string) => {
		setLocalValue(localValue.filter((val) => val !== optionValue));
	};

	const selectedLabel = (optionValue: string) => {
		const [key, , tag] = optionValue.split("|");
		const groupLabel = GROUPS.find(([k]) => k === key)?.[1] ?? key;
		return `${groupLabel}: ${tag}`;
	};

	return (
		<>
			<Chip
				sx={{ maxWidth: 320 }}
				aria-haspopup="listbox"
				aria-expanded={open}
				label={
					<Stack direction="row" alignItems="center" gap="8px" minWidth={0}>
						<Typography
							fontSize={13}
							fontWeight={500}
							noWrap
							sx={{ minWidth: 0 }}
						>
							{label}: {value.map(selectedLabel).join(" OR ") || "Any"}
						</Typography>
						{value.length === 0 ? (
							open ? (
								<ExpandLess sx={{ color: "#00000042" }} />
							) : (
								<ExpandMore sx={{ color: "#00000042" }} />
							)
						) : null}
					</Stack>
				}
				variant={value.length ? "outlined" : "filled"}
				color={value.length ? "primary" : "default"}
				ref={anchorRef}
				onClick={() => {
					if (value.length) return;
					setOpen(true);
				}}
				onDelete={
					value.length
						? () => {
							setLocalValue([]);
							onChange([]);
						}
						: undefined
				}
				disabled={options.length === 0}
			/>
			<Popover
				anchorEl={anchorRef.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center",
				}}
				open={open}
				PaperProps={{ sx: { width: 420, maxHeight: "70vh" } }}
				onClose={() => {
					setOpen(false);
					setSearch("");
					onChange(localValue);
				}}
			>
				<Box
					sx={{
						position: "sticky",
						top: 0,
						zIndex: 1,
						bgcolor: "background.paper",
						borderBottom: "1px solid",
						borderColor: "divider",
						px: "12px",
						py: "8px",
					}}
				>
					<TextField
						autoFocus
						fullWidth
						size="small"
						placeholder={`Search ${label}`}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</Box>
				{visibleOptions.length === 0 && (
					<Typography color="text.disabled" sx={{ px: "16px", py: "8px" }}>No matches</Typography>
				)}
				<RichTreeView
					items={items}
					multiSelect
					checkboxSelection
					selectionPropagation={{ descendants: true }}
					isItemSelectionDisabled={(item) => item.id.startsWith("group:")}
					selectedItems={treeSelection}
					onSelectedItemsChange={(_event, ids) => handleTreeSelection(ids)}
					expandedItems={[...items.map((item) => item.id), ...expandedColors]}
					onExpandedItemsChange={(_event, ids) => setExpandedColors(ids.filter((id) => id.startsWith("color:")))}
					slots={{ item: StatusTreeItem }}
					sx={{ py: "4px" }}
				/>
				{localValue.length > 0 && (
					<Box
						sx={{
							position: "sticky",
							bottom: 0,
							zIndex: 1,
							bgcolor: "background.paper",
							borderTop: "1px solid",
							borderColor: "divider",
							px: "12px",
							py: "8px",
						}}
					>
						<Stack direction="row" flexWrap="wrap" gap="6px">
							{localValue.map((selected, index) => (
								<Chip
									key={index}
									size="small"
									label={selectedLabel(selected)}
									onDelete={() => removeSelection(selected)}
								/>
							))}
						</Stack>
					</Box>
				)}
			</Popover>
		</>
	);
};
